/**
 * DrawingToolsPanel - UI panel with drawing tool buttons and style controls.
 *
 * Provides toolbar buttons for selecting drawing tools (point, line, polygon,
 * circle, rectangle, text) and controls for customizing drawing styles.
 *
 * Validates: Requirements 39.1, 39.2, 39.4, 39.5, 39.6, 40.1
 */
import React, { useCallback } from 'react';
import {
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Divider,
  Slider,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  RadioButtonUnchecked as PointIcon,
  Timeline as LineIcon,
  Pentagon as PolygonIcon,
  Circle as CircleIcon,
  CropSquare as RectangleIcon,
  TextFields as TextIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ClearAll as ClearAllIcon,
  Save as SaveIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  setActiveTool,
  setDrawingStyle,
  clearAllDrawings,
  markSavedAsLayer,
} from '../store/slices/drawingSlice';
import type { DrawingToolType } from '../types/drawing';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DrawingToolsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const { activeTool, drawingStyle, drawings } = useAppSelector((state) => state.drawing);
  const [showStylePanel, setShowStylePanel] = React.useState(false);

  const handleToolChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newTool: DrawingToolType) => {
      dispatch(setActiveTool(newTool));
    },
    [dispatch]
  );

  const handleEditMode = useCallback(() => {
    dispatch(setActiveTool(null));
  }, [dispatch]);

  const handleClearAll = useCallback(() => {
    dispatch(clearAllDrawings());
  }, [dispatch]);

  const handleSaveAsLayer = useCallback(() => {
    dispatch(markSavedAsLayer());
  }, [dispatch]);

  const handleFillColorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setDrawingStyle({ fillColor: event.target.value }));
    },
    [dispatch]
  );

  const handleStrokeColorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setDrawingStyle({ strokeColor: event.target.value }));
    },
    [dispatch]
  );

  const handleStrokeWidthChange = useCallback(
    (_event: Event, value: number | number[]) => {
      dispatch(setDrawingStyle({ strokeWidth: value as number }));
    },
    [dispatch]
  );

  const handleOpacityChange = useCallback(
    (_event: Event, value: number | number[]) => {
      dispatch(setDrawingStyle({ opacity: value as number }));
    },
    [dispatch]
  );

  return (
    <Paper
      elevation={3}
      sx={{
        p: 1.5,
        width: 280,
        maxHeight: '80vh',
        overflow: 'auto',
      }}
    >
      {/* Header */}
      <Typography variant="subtitle2" gutterBottom>
        Drawing Tools
      </Typography>

      {/* Tool Selection */}
      <ToggleButtonGroup
        value={activeTool}
        exclusive
        onChange={handleToolChange}
        size="small"
        sx={{ flexWrap: 'wrap', mb: 1 }}
        aria-label="Drawing tools"
      >
        <ToggleButton value="point" aria-label="Draw point">
          <Tooltip title="Pin Location / Marker">
            <PointIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="line" aria-label="Draw line">
          <Tooltip title="Draw Fence / Pipeline">
            <LineIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="polygon" aria-label="Draw polygon">
          <Tooltip title="Draw Boundary / Fence Line">
            <PolygonIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="circle" aria-label="Draw circle">
          <Tooltip title="Circle">
            <CircleIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="rectangle" aria-label="Draw rectangle">
          <Tooltip title="Rectangle">
            <RectangleIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="text" aria-label="Text annotation">
          <Tooltip title="Text Annotation">
            <TextIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Edit/Move Mode */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
        <Tooltip title="Edit/Move drawings">
          <IconButton
            size="small"
            onClick={handleEditMode}
            color={activeTool === null && drawings.length > 0 ? 'primary' : 'default'}
            aria-label="Edit drawings"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Style Toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Tooltip title="Style settings">
          <IconButton
            size="small"
            onClick={() => setShowStylePanel(!showStylePanel)}
            color={showStylePanel ? 'primary' : 'default'}
            aria-label="Toggle style panel"
          >
            <PaletteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" sx={{ ml: 1 }}>
          Style Settings
        </Typography>
      </Box>

      {/* Style Controls */}
      <Collapse in={showStylePanel}>
        <Box sx={{ px: 1, pb: 1 }}>
          {/* Fill Color */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ width: 70 }}>
              Fill
            </Typography>
            <input
              type="color"
              value={drawingStyle.fillColor}
              onChange={handleFillColorChange}
              style={{ width: 32, height: 24, border: 'none', cursor: 'pointer' }}
              aria-label="Fill color"
            />
          </Box>

          {/* Stroke Color */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ width: 70 }}>
              Stroke
            </Typography>
            <input
              type="color"
              value={drawingStyle.strokeColor}
              onChange={handleStrokeColorChange}
              style={{ width: 32, height: 24, border: 'none', cursor: 'pointer' }}
              aria-label="Stroke color"
            />
          </Box>

          {/* Stroke Width */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption">Stroke Width: {drawingStyle.strokeWidth}px</Typography>
            <Slider
              value={drawingStyle.strokeWidth}
              onChange={handleStrokeWidthChange}
              min={1}
              max={10}
              step={1}
              size="small"
              aria-label="Stroke width"
            />
          </Box>

          {/* Opacity */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption">
              Opacity: {Math.round(drawingStyle.opacity * 100)}%
            </Typography>
            <Slider
              value={drawingStyle.opacity}
              onChange={handleOpacityChange}
              min={0}
              max={1}
              step={0.05}
              size="small"
              aria-label="Opacity"
            />
          </Box>
        </Box>
      </Collapse>

      <Divider sx={{ my: 1 }} />

      {/* Actions */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ClearAllIcon />}
          onClick={handleClearAll}
          disabled={drawings.length === 0}
          aria-label="Clear all drawings"
        >
          Clear All
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveAsLayer}
          disabled={drawings.length === 0}
          aria-label="Save as layer"
        >
          Save as Layer
        </Button>
      </Box>

      {/* Drawing Count */}
      {drawings.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {drawings.length} drawing{drawings.length !== 1 ? 's' : ''} on map
        </Typography>
      )}
    </Paper>
  );
};

export default DrawingToolsPanel;
